import { createModules } from "./modules/_/index";
import hey from "./hey";
import { NoMobile } from "./modules/nomobile";
import { ScrollTrigger } from "./gsap";

/**
 * Dom - Module Lifecycle Manager
 * 
 * Manages the creation, starting, stopping, and destruction of modules.
 * Handles both initial page load and navigation transitions.
 * 
 * IMPORTANT: The lifecycle is split into two phases:
 * 
 * 1. create() - Instantiate modules (they set their initial hidden state)
 *    This MUST happen BEFORE the preloader hides!
 * 
 * 2. start() - Begin observing (triggers isIn/isOut callbacks)
 *    This happens AFTER the preloader hides.
 * 
 * This separation prevents the "flash" where elements briefly show
 * before animating in.
 */
export class _Dom {
  #items = [];
  #created = false;
  #started = false;

  constructor() {
    // Don't auto-start - wait for App to orchestrate
  }

  /** Getters */

  get items() {
    return this.#items;
  }

  get created() {
    return this.#created;
  }

  get started() {
    return this.#started;
  }

  /**
   * Find a module by name
   * @param {string} name - The module name (data-module value)
   * @returns {Object|undefined} The module instance
   */
  getModule(name) {
    return this.#items.find((item) => 
      item.constructor.name.toLowerCase() === name.toLowerCase()
    );
  }

  /** Lifecycle Methods */

  /**
   * PHASE 1: Create module instances
   * 
   * Call this BEFORE the preloader hides!
   * Modules will set their initial hidden state (opacity: 0, yPercent: 110, etc.)
   * so when the preloader reveals the page, elements are already hidden.
   */
  create() {
    if (this.#created) return;
    this.#created = true;

    // Process nomobile elements first (disables modules on mobile)
    document.querySelectorAll("[data-nomobile]").forEach((element) => {
      new NoMobile(element);
    });

    // Auto-discover and instantiate all modules
    // Module constructors should set initial hidden state
    this.#items = createModules();
    
    console.log(`📦 Created ${this.#items.length} modules`);
  }

  /**
   * PHASE 2: Start module observers
   * 
   * Call this AFTER the preloader hides!
   * Observers begin watching, isIn() triggers, elements animate in.
   */
  start() {
    if (this.#started) return;
    this.#started = true;

    this.#items.forEach((item) => item.start?.());
    
    hey.DOM_READY = true;
    console.log(`▶️ Started ${this.#items.length} modules`);
  }

  /**
   * Stop all module observers/tracking
   */
  stop() {
    this.#items.forEach((item) => item.stop?.());
  }

  /**
   * Destroy all modules and reset state
   */
  destroy() {
    this.#items.forEach((item) => item.destroy?.());
    this.#items = [];
    this.#created = false;
    this.#started = false;
    
    // Kill any lingering ScrollTriggers
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
  }

  /**
   * Handle page transition out
   * Called before navigating away from current page
   * @returns {Promise} Resolves when all modules have transitioned out
   */
  async transitionOut() {
    // Let modules animate out
    await Promise.all(
      this.#items.map((item) => item.transitionOut?.())
    );
    
    // Clean up
    this.destroy();
  }

  /**
   * Handle page transition in
   * Called after new page content is ready
   * 
   * For navigation, we create AND start together since there's no preloader.
   * The transition animation serves as the "reveal" instead.
   * 
   * @returns {Promise} Resolves when all modules have transitioned in
   */
  async transitionIn() {
    // Reset flags for new page
    this.#created = false;
    this.#started = false;
    
    // Create new modules (sets hidden state)
    this.create();
    
    // Start observers (triggers animations)
    this.start();
    
    // Let modules animate in
    await Promise.all(
      this.#items.map((item) => item.transitionIn?.())
    );
  }
}

export const Dom = new _Dom();
